import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import timelineService from '../services/timelineService';
import storyEventService from '../services/storyEventService';
import categoryService from '../services/categoryService';
import markerService from '../services/markerService';
import TimelineMap from '../components/maps/TimelineMap';
import EventsMenu from '../components/maps/EventsMenu';
import MarkerStyleMenu from '../components/maps/MarkerStyleMenu';
import EventPopup from '../components/EventPopup';
import CustomDate from '../utils/CustomDate';
import { markerStyles as defaultMarkerStyles } from '../components/maps/MarkerStyles';
import MarkerListPanel from '../components/maps/MarkerListPanel';
import SearchPopup from '../components/SearchPopup';
import EmbedModal from '../components/EmbedModal';
import { generateMapIframeCode } from '../utils/embed';
import { baseDate, baseDateValues } from '../utils/settings';

const TimelineMapPage = () => {
  const { id: timelineId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // Get URL parameters for event pre-selection, embed mode, and initial view
  const urlParams = new URLSearchParams(window.location.search);
  const eventIdFromUrl = urlParams.get('eventId');
  const isEmbed = urlParams.get('embed') === 'true';

  // Parse initial view from URL params (for embedded maps)
  const zoomFromUrl = urlParams.get('zoom');
  const latFromUrl = urlParams.get('lat');
  const lngFromUrl = urlParams.get('lng');

  // Determine initial view settings from URL (if provided)
  // Handle NaN cases for malformed URLs
  const parsedZoom = zoomFromUrl ? parseInt(zoomFromUrl, 10) : NaN;
  const parsedLat = latFromUrl ? parseFloat(latFromUrl) : NaN;
  const parsedLng = lngFromUrl ? parseFloat(lngFromUrl) : NaN;

  const initialZoom = !isNaN(parsedZoom) ? parsedZoom : undefined;
  const initialCenter = (!isNaN(parsedLat) && !isNaN(parsedLng))
    ? [parsedLat, parsedLng]
    : undefined;

  // State management
  const [timeline, setTimeline] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [timeFilter, setTimeFilter] = useState({ active: false, startYear: null, endYear: null });
  const [textFilter, setTextFilter] = useState({
    active: false,
    text: '',
    searchInTitle: true,
    searchInDescription: true
  });
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);

  // UI State
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMarkerType, setSelectedMarkerType] = useState(null);
  const [selectedMarkerSize, setSelectedMarkerSize] = useState('m');
  const [panelsOpen, setPanelsOpen] = useState(true);
  const [selectedEventForPopup, setSelectedEventForPopup] = useState(null);
  const [customMarkerStyles, setCustomMarkerStyles] = useState(defaultMarkerStyles);

  // Embed State
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [embedOptions, setEmbedOptions] = useState({
    width: '100%',
    height: '500px'
  });

  // Map View State (for capturing current zoom/position for embed)
  const [currentMapView, setCurrentMapView] = useState(null);

  // Check if user is timeline owner (for editing permissions)
  const isOwner = isAuthenticated && user && timeline && user._id === timeline.user_id;

  // Setup for SearchPopup
  useEffect(() => {
    window.setTimelineTimeFilter = setTimeFilter;
    window.setTimelineTextFilter = setTextFilter;

    if (!window.timelineEvents && events.length > 0) {
      window.timelineEvents = [...events];
    }

    return () => {
      delete window.setTimelineTimeFilter;
      delete window.setTimelineTextFilter;
    };
  }, [events]);

  // Load timeline and related data
  useEffect(() => {
    const loadTimelineData = async () => {
      try {
        setLoading(true);

        // Load timeline
        const timelineData = await timelineService.getTimelineById(timelineId);
        setTimeline(timelineData);

        // Load events and categories
        const [eventsData, categoriesData] = await Promise.all([
          storyEventService.getStoryEvents(timelineId),
          categoryService.getCategories(timelineId)
        ]);

        setEvents(eventsData);
        setCategories(categoriesData);
        setFilteredEvents(eventsData);

        // Load markers for this timeline
        const markersData = await markerService.getTimelineMarkers(timelineId);
        setMarkers(markersData);
        setFilteredMarkers(markersData);

      } catch (err) {
        console.error('Error loading timeline data:', err);
        setError('Zaman çizelgesi verileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    if (timelineId) {
      loadTimelineData();
    }
  }, [timelineId]);

  // Pre-select event if eventId is provided in URL
  useEffect(() => {
    if (eventIdFromUrl && events.length > 0) {
      const eventToSelect = events.find(event => event._id === eventIdFromUrl);
      if (eventToSelect) {
        setSelectedEvent(eventToSelect);
      }
    }
  }, [eventIdFromUrl, events]);

  // Helper functions for filtering from Timeline.jsx/SearchPopup.jsx
  const extractCategoryId = useCallback((categoryIdInput) => {
    if (!categoryIdInput) {
      return null;
    }

    try {
      // If it's already a string
      if (typeof categoryIdInput === 'string') {
        return categoryIdInput;
      }

      // If it's an object with _id property (MongoDB style)
      if (typeof categoryIdInput === 'object' && categoryIdInput._id) {
        return String(categoryIdInput._id);
      }

      // If it's an object with id property
      if (typeof categoryIdInput === 'object' && categoryIdInput.id) {
        return String(categoryIdInput.id);
      }

      // Try to stringify the object - this works for MongoDB ObjectId
      if (typeof categoryIdInput === 'object') {
        // Check for toString method
        if (typeof categoryIdInput.toString === 'function') {
          const idStr = categoryIdInput.toString();
          // Only return if it looks like a valid ID (avoid [object Object])
          if (idStr !== '[object Object]') {
            return idStr;
          }
        }

        // Last resort - get any property that looks like an ID
        for (const key in categoryIdInput) {
          if (['_id', 'id', '$oid'].includes(key) && categoryIdInput[key]) {
            return String(categoryIdInput[key]);
          }
        }
      }

      // If all else fails
      console.warn('Could not extract category ID from:', categoryIdInput);
      return null;
    } catch (error) {
      console.error('Error extracting category ID:', error);
      return null;
    }
  }, []);

  const isCategoryInFilter = useCallback((categoryId) => {
    if (!categoryId || !filteredCategories || filteredCategories.length === 0) {
      return false;
    }

    try {
      const extractedId = extractCategoryId(categoryId);
      if (!extractedId) {
        return false;
      }

      const result = filteredCategories.some(filteredId => {
        const filteredExtractedId = extractCategoryId(filteredId);
        if (!filteredExtractedId) return false;

        const filteredIdStr = String(filteredExtractedId);
        const extractedIdStr = String(extractedId);

        if (filteredIdStr === extractedIdStr) {
          return true;
        }

        if (extractedIdStr.length > 10 && filteredIdStr.length > 10) {
          const endMatch = extractedIdStr.endsWith(filteredIdStr.substring(filteredIdStr.length - 10));
          const startMatch = filteredIdStr.endsWith(extractedIdStr.substring(extractedIdStr.length - 10));

          return endMatch || startMatch;
        }

        return false;
      });

      return result;
    } catch (error) {
      console.error('Error matching category for filtering:', error);
      return false;
    }
  }, [filteredCategories, extractCategoryId]);

  // Filter markers based on active filters
  useEffect(() => {
    const hasActiveFilters =
      (filteredCategories && filteredCategories.length > 0) ||
      (timeFilter.active && (timeFilter.startYear !== null || timeFilter.endYear !== null)) ||
      (textFilter.active && textFilter.text);

    if (!hasActiveFilters) {
      setFilteredMarkers(markers);
      setFilteredEvents(events);
      return;
    }

    const newFilteredEvents = events.filter(event => {
      // Category filter - match either category_id or linked_category_id
      if (filteredCategories && filteredCategories.length > 0) {
        const categoryMatches = isCategoryInFilter(event.category_id);
        const linkedCategoryMatches = isCategoryInFilter(event.linked_category_id);
        if (!categoryMatches && !linkedCategoryMatches) {
          return false;
        }
      }

      // Time filter
      if (timeFilter.active && (timeFilter.startYear !== null || timeFilter.endYear !== null)) {
        if (!event.startDate) {
          return false;
        }

        // CustomDate nesnesinden yılı al
        let eventYear;
        if (typeof event.startDate.getFullYear === 'function') {
          eventYear = event.startDate.getFullYear();
        } else if (event.startDate.year !== undefined) {
          // Fallback if it is a plain object
          eventYear = event.startDate.year;
        } else {
          return false;
        }

        if (timeFilter.startYear !== null && eventYear < timeFilter.startYear) {
          return false;
        }
        if (timeFilter.endYear !== null && eventYear > timeFilter.endYear) {
          return false;
        }
      }

      // Text filter
      if (textFilter.active && textFilter.text) {
        const searchText = textFilter.text.toLowerCase();
        if (textFilter.searchInTitle || textFilter.searchInDescription) {
          const title = (event.title || '').toLowerCase();
          const description = (event.description || '').toLowerCase();
          const titleMatch = textFilter.searchInTitle && title.includes(searchText);
          const descriptionMatch = textFilter.searchInDescription && description.includes(searchText);
          if (!titleMatch && !descriptionMatch) {
            return false;
          }
        }
      }

      return true;
    });
    setFilteredEvents(newFilteredEvents);

    const filteredEventIds = new Set(newFilteredEvents.map(e => e._id));
    const newFilteredMarkers = markers.filter(marker =>
      marker.storyEvent_id && filteredEventIds.has(marker.storyEvent_id._id)
    );
    setFilteredMarkers(newFilteredMarkers);

  }, [markers, events, filteredCategories, timeFilter, textFilter, isCategoryInFilter]);


  // Fix map tile loading issues
  useEffect(() => {
    // Trigger map resize after initial load
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('mapResize'));
      window.dispatchEvent(new Event('resize'));
    }, 500);

    return () => clearTimeout(timer);
  }, [markers, timeline]);

  // Handle panel toggle resize
  useEffect(() => {
    if (isOwner) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('mapResize'));
      }, 350); // Match the transition duration

      return () => clearTimeout(timer);
    }
  }, [panelsOpen, isOwner]);

  // Handle map click to place marker
  const handleMapClick = useCallback(async (latlng) => {
    // Only allow map clicks for timeline owners
    if (!isOwner) {
      return;
    }

    if (!selectedEvent || !selectedMarkerType) {
      alert('Lütfen haritaya eklemeden önce bir olay ve bir işaretleyici tipi seçin.');
      return;
    }

    try {
      const markerData = {
        type: selectedMarkerType,
        size: selectedMarkerSize,
        lngLat: [latlng.lat, latlng.lng],
        color: selectedEvent.category_id?.color
      };

      const newMarker = await markerService.createMarker(timelineId, selectedEvent._id, markerData);

      // Add event data to marker for display
      const enrichedMarker = {
        ...newMarker,
        storyEvent_id: selectedEvent
      };

      setMarkers(prev => [...prev, enrichedMarker]);

      // Reset selections
      setSelectedEvent(null);
      setSelectedMarkerType(null);
    } catch (err) {
      console.error('Error creating marker:', err);
      setError('İşaretleyici oluşturulurken hata oluştu');
    }
  }, [selectedEvent, selectedMarkerType, selectedMarkerSize, timelineId, isOwner]);

  // Handle marker deletion
  const handleMarkerDelete = useCallback(async (markerId) => {
    if (!isOwner) {
      return;
    }

    try {
      await markerService.deleteMarker(markerId);
      setMarkers(prev => prev.filter(m => m._id !== markerId));
    } catch (err) {
      console.error('Error deleting marker:', err);
      setError('İşaretleyici silinirken hata oluştu');
    }
  }, [isOwner]);

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback(async (markerId, newPosition) => {
    if (!isOwner) {
      return;
    }

    try {
      await markerService.updateMarker(markerId, {
        lngLat: newPosition
      });

      setMarkers(prev => prev.map(marker =>
        marker._id === markerId
          ? { ...marker, lngLat: newPosition }
          : marker
      ));
    } catch (err) {
      console.error('Error updating marker position:', err);
      setError('İşaretleyici konumu güncellenirken hata oluştu');
    }
  }, [isOwner]);

  // Handle marker size change
  const handleMarkerSizeChange = useCallback(async (markerId, newSize) => {
    if (!isOwner) {
      return;
    }

    try {
      await markerService.updateMarker(markerId, { size: newSize });

      setMarkers(prev => prev.map(marker =>
        marker._id === markerId
          ? { ...marker, size: newSize }
          : marker
      ));
    } catch (err) {
      console.error('Error updating marker size:', err);
      setError('İşaretleyici boyutu güncellenirken hata oluştu');
    }
  }, [isOwner]);

  // Convert event dates to CustomDate objects (like in timeline)
  const convertEventDates = (event) => {
    if (!event) return event;

    const convertedEvent = { ...event };
    // Convert startDate
    if (event.startDate) {
      convertedEvent.startDate = new CustomDate(
        event.startDate.year,
        event.startDate.month,
        event.startDate.day
      );
    }

    // Convert endDate if it exists
    if (event.endDate) {
      convertedEvent.endDate = new CustomDate(
        event.endDate.year,
        event.endDate.month,
        event.endDate.day
      );
    }

    return convertedEvent;
  };

  // Handle marker click to show event popup
  const handleMarkerClick = useCallback((eventData) => {
    if (eventData) {
      // Convert dates to CustomDate objects before showing popup
      const convertedEvent = convertEventDates(eventData);
      setSelectedEventForPopup(convertedEvent);
    }
  }, []);

  const handleDeleteStyle = (type) => {
    setCustomMarkerStyles(prev => prev.filter(style => style.type !== type));
  };

  // Handle category filter from EventPopup
  const handleCategoryFilterFromPopup = useCallback((categoryId) => {
    if (categoryId) {
      setFilteredCategories([categoryId]);
      setSelectedEventForPopup(null); // Close popup after filtering
      document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
    }
  }, []);

  // Handle map view changes (for embed functionality)
  const handleMapViewChange = useCallback((viewData) => {
    setCurrentMapView(viewData);
  }, []);

  // Handle map embed generation
  const handleMapEmbed = useCallback(() => {
    if (!timeline) return;

    try {
      setShowEmbedModal(true);
      // Include current map view in embed options
      const optionsWithView = {
        ...embedOptions,
        mapView: currentMapView
      };
      const code = generateMapIframeCode(
        timeline,
        window.location.origin,
        optionsWithView
      );
      setEmbedCode(code);
    } catch (err) {
      console.error('Embed kodu oluşturulurken hata:', err);
      setError('Embed kodu oluşturulurken hata oluştu');
    }
  }, [timeline, embedOptions, currentMapView]);

  // Handle embed options change
  const handleEmbedOptionsChange = useCallback((newOptions) => {
    setEmbedOptions(newOptions);
    if (timeline) {
      // Include current map view in embed options
      const optionsWithView = {
        ...newOptions,
        mapView: currentMapView
      };
      const code = generateMapIframeCode(
        timeline,
        window.location.origin,
        optionsWithView
      );
      setEmbedCode(code);
    }
  }, [timeline, currentMapView]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        <p className="ml-4 text-lg text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/cizelge/${timelineId}`)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Zaman Çizelgesine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen bg-gray-50 text-gray-600 overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-30 bg-white shadow-sm border-b">
        <div className="flex justify-between items-center px-4 py-2">
          <div>
            <h1 className="text-lg font-semibold">{timeline?.title} - Harita</h1>
            <p className="text-sm text-gray-500">{markers.length} işaretleyici</p>
          </div>
          <div className="flex space-x-2">
            {isOwner && !isEmbed && (
              <button
                onClick={() => setPanelsOpen(!panelsOpen)}
                className="p-2 bg-white rounded-lg shadow hover:bg-gray-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            {!isEmbed && (
              <button
                onClick={handleMapEmbed}
                className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                title="Embed Kodu Oluştur"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" clipRule="evenodd" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setIsSearchPopupOpen(true)}
              className="flex items-center justify-center space-x-2 text-gray-700 hover:bg-gray-300 rounded transition-colors py-1.5 px-3 gap-1"
              title="Filtrele ve Ara"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <span>Ara</span>
            </button>
            {!isEmbed && (
              <button
                onClick={() => navigate(`/cizelge/${timelineId}`)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Zaman Çizelgesine Dön
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className={`absolute inset-0 transition-all duration-300 ${isOwner && panelsOpen && !isEmbed ? 'right-80' : 'right-0'}`} style={{ top: '60px' }}>
        <TimelineMap
          markers={filteredMarkers}
          onMapClick={isOwner && !isEmbed ? handleMapClick : null}
          onEmptyClick={() => setSelectedEventForPopup(null)}
          onMarkerDelete={isOwner && !isEmbed ? handleMarkerDelete : null}
          onMarkerDragEnd={isOwner && !isEmbed ? handleMarkerDragEnd : null}
          onMarkerSizeChange={isOwner && !isEmbed ? handleMarkerSizeChange : null}
          onMarkerClick={handleMarkerClick}
          onViewChange={handleMapViewChange}
          initialZoom={initialZoom}
          initialCenter={initialCenter}
        />
      </div>

      {/* Side Panels - Only show for owners and not in embed mode */}
      {isOwner && !isEmbed && (
        <div className={`fixed top-0 right-0 bottom-0 flex transition-transform duration-300 z-20 ${panelsOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{ top: '60px' }}>
          {/* Events Panel */}
          <div className="w-80 h-full bg-white shadow-lg border-l border-gray-200">
            <EventsMenu
              events={filteredEvents}
              categories={categories}
              selectedEvent={selectedEvent}
              onSelectEvent={setSelectedEvent}
            />
          </div>

          {/* Marker Style Panel */}
          <div className="w-80 h-full bg-white shadow-lg border-l border-gray-200">
            <div className="p-4 h-full flex flex-col overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">İşaretleyici Stilleri</h2>
              <MarkerStyleMenu
                markerStyles={customMarkerStyles}
                selectedStyle={selectedMarkerType}
                onSelect={setSelectedMarkerType}
                selectedSize={selectedMarkerSize}
                onSizeSelect={setSelectedMarkerSize}
                disabled={!selectedEvent}
                onDeleteStyle={handleDeleteStyle}
              />
              {!selectedEvent && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    İşaretleyici seçebilmek için önce bir olay seçin
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Marker List Panel */}
          <MarkerListPanel
            markers={markers}
            onDeleteMarker={handleMarkerDelete}
          />
        </div>
      )}

      {/* EventPopup for marker clicks */}
      {selectedEventForPopup && (
        <EventPopup
          event={selectedEventForPopup}
          onClose={() => setSelectedEventForPopup(null)}
          timelineData={timeline}
          onTimelineUpdate={async (updatedSettings) => {
            try {
              await timelineService.updateTimeline(timelineId, updatedSettings);
              setTimeline(updatedSettings);
            } catch (error) {
              console.error('Timeline güncellenirken hata:', error);
            }
          }}
          isAuthenticated={isAuthenticated}
          categories={categories}
          onCategoryFilter={handleCategoryFilterFromPopup}
          position="right"
        />
      )}

      {/* Search Popup */}
      {isSearchPopupOpen && (
        <SearchPopup
          isOpen={isSearchPopupOpen}
          onClose={() => setIsSearchPopupOpen(false)}
          onEventSelect={(event) => {
            // Future enhancement: find marker and fly to it on map
            setIsSearchPopupOpen(false);
          }}
          categories={categories}
          filteredCategories={filteredCategories}
          setFilteredCategories={setFilteredCategories}
          timeFilter={timeFilter}
          textFilter={textFilter}
        />
      )}

      {/* Embed Modal */}
      <EmbedModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        embedCode={embedCode}
        options={embedOptions}
        onOptionsChange={handleEmbedOptionsChange}
        mode="map"
      />
    </div>
  );
};

export default TimelineMapPage; 